import React, { useEffect, useState } from 'react';
import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { Control, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';

import {
  OperacionLCLSCuentaLimiteUpdate,
  OperacionLCLSCuentaLimiteVista,
  OperacionLCLSLimiteTipo, schemaOperacionLCLSCuentaLimiteUpdate,
} from '../types/dto';
import { OperacionLCLS } from '../types/types';
import { HttpLCLS } from '../api/httpLCLS';

import { ControlledDatePicker } from '../../../../../components/forms';
import { OperacionLCLSCampoLimite } from './OperacionLCLSCampoLimite';
import { dateFormatter } from 'src/utils/formatters';
import { EntityWithIdFields } from 'src/types';
import { numberFormatter } from "../../../../../utils/formatters/numberFormatter";
import { OperacionLCLSCuentaLimiteFields } from "../types/fields";
import EditIcon from "@mui/icons-material/Edit";

const ACTION_WIDTH = 40; // El ancho que siempre reservamos para el botón

export const LimitCellLayout = ({ children, action }: { children: React.ReactNode, action?: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
    <Box sx={{ textAlign: 'right' }}>
      {children}
    </Box>

    {/* espacio para boton que se usa este o no el boton */}
    <Box sx={{ width: ACTION_WIDTH, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
      {action}
    </Box>
  </Box>
);

interface EditableLCLSCampoLimiteProps {
  isEditing: boolean;
  onEdit: () => void;
  value: number | null;
  label: string;
  name: string;
  control: Control<any>;
  loadOptions: () => Promise<OperacionLCLSLimiteTipo[]>;
  onModeChange: (isFreeSolo: boolean) => void;
  disabled?: boolean;
}

const EditableLCLSCampoLimite = ({
  isEditing,
  onEdit,
  value,
  label,
  name,
  control,
  loadOptions,
  onModeChange,
  disabled
}: EditableLCLSCampoLimiteProps) => {
  if (!isEditing) {
    return (
      <LimitCellLayout
        action={
          <IconButton onClick={onEdit} size="small" disabled={disabled}>
            <EditIcon fontSize="small" color={disabled ? 'disabled' : 'info'} />
          </IconButton>
        }
      >
        <Typography variant="body2" noWrap>
          {numberFormatter.toStringWithAmount(value, 'USD', 0)}
        </Typography>
      </LimitCellLayout>
    );
  }

  return (
    <OperacionLCLSCampoLimite
      control={control}
      name={name}
      label={label}
      loadOptions={loadOptions}
      onModeChange={onModeChange}
      buttonType={'icon'}
      disabled={disabled}
    />
  );
};


interface OperacionLCLSLimiteFilaProps {
  operacion: OperacionLCLS;
  limite: OperacionLCLSCuentaLimiteVista;
  loadLimitesOptions: () => Promise<OperacionLCLSLimiteTipo[]>;
  refetch: () => void;
}

export const OperacionLCLSLimiteFila = ({
  operacion,
  limite,
  loadLimitesOptions,
  refetch
}: OperacionLCLSLimiteFilaProps) => {

  if (!limite[OperacionLCLSCuentaLimiteFields.PermiteModificar]) {
    return (
      <TableRow>
        <TableCell>{limite[OperacionLCLSCuentaLimiteFields.DescGrupo]}</TableCell>
        <TableCell align="right">
          <LimitCellLayout>
            <Typography variant="body2">
              {numberFormatter.toStringWithAmount(limite[OperacionLCLSCuentaLimiteFields.LimiteConcertacion], 'USD', 0)}
            </Typography>
          </LimitCellLayout>
        </TableCell>
        <TableCell align="right">
          <LimitCellLayout>
            <Typography variant="body2">
              {numberFormatter.toStringWithAmount(limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP], 'USD', 0)}
            </Typography>
          </LimitCellLayout>
        </TableCell>
        <TableCell align="right">
          <LimitCellLayout>
            <Typography variant="body2">
              {numberFormatter.toStringWithAmount(limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP], 'USD', 0)}
            </Typography>
          </LimitCellLayout>
        </TableCell>

        <TableCell>{dateFormatter.toShortDate(limite[OperacionLCLSCuentaLimiteFields.FechaAccion])}</TableCell>
      </TableRow>
    );
  }

  const [isSaving, setIsSaving] = useState(false);
  const [fieldModes, setFieldModes] = useState({
    concertacion: false,
    dvp: false,
    fop: false
  });
  const [editModes, setEditModes] = useState({
    concertacion: false,
    dvp: false,
    fop: false
  });

  const formMethods = useForm<OperacionLCLSCuentaLimiteUpdate>({
    resolver: zodResolver(schemaOperacionLCLSCuentaLimiteUpdate),
    defaultValues: {
      [OperacionLCLSCuentaLimiteFields.LimiteConcertacion]: limite[OperacionLCLSCuentaLimiteFields.LimiteConcertacion],
      [OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP]: limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP],
      [OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP]: limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP],
    },
  });

  const { control, handleSubmit, formState: { isDirty }, reset } = formMethods;
  const watchedValues = formMethods.watch();

  const handleUpdate: SubmitHandler<OperacionLCLSCuentaLimiteUpdate> = (data) => {
    setIsSaving(true);
    const promise = HttpLCLS.updateLimite(operacion[EntityWithIdFields.Id], limite[EntityWithIdFields.Id], data);

    toast.promise(promise, {
      loading: 'Guardando...',
      success: 'Límite actualizado.',
      error: (e) => `Error: ${e}`,
    })
      .then(() => {
        refetch();
        reset(data);
        setEditModes({ concertacion: false, dvp: false, fop: false });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    // @ts-ignore
    if (Object.values(watchedValues).includes(null)) {
      return;
    }

    const useDebounce = fieldModes.concertacion || fieldModes.dvp || fieldModes.fop;

    if (useDebounce) {
      const timer = setTimeout(() => handleSubmit(handleUpdate)(), 1000);

      return () => clearTimeout(timer);
    } else {
      handleSubmit(handleUpdate)();
    }

  }, [JSON.stringify(watchedValues)]);

  return (
    <TableRow sx={{ '& .MuiFormControl-root': { my: 1 }, }}>
      <TableCell>
        {limite[OperacionLCLSCuentaLimiteFields.DescGrupo]}
      </TableCell>
      <TableCell>
        <EditableLCLSCampoLimite
          isEditing={editModes.concertacion}
          onEdit={() => setEditModes(prev => ({ ...prev, concertacion: true }))}
          value={limite[OperacionLCLSCuentaLimiteFields.LimiteConcertacion]}
          label="Límite de concertación"
          name={OperacionLCLSCuentaLimiteFields.LimiteConcertacion}
          control={control}
          loadOptions={loadLimitesOptions}
          onModeChange={(isFreeSolo) => setFieldModes(prev => ({ ...prev, concertacion: isFreeSolo }))}
          disabled={isSaving}
        />
      </TableCell>
      <TableCell>
        <EditableLCLSCampoLimite
          isEditing={editModes.dvp}
          onEdit={() => setEditModes(prev => ({ ...prev, dvp: true }))}
          value={limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP]}
          label="Límite de liquidación DVP"
          name={OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP}
          control={control}
          loadOptions={loadLimitesOptions}
          onModeChange={(isFreeSolo) => setFieldModes(prev => ({ ...prev, dvp: isFreeSolo }))}
          disabled={isSaving}
        />
      </TableCell>
      <TableCell>
        <EditableLCLSCampoLimite
          isEditing={editModes.fop}
          onEdit={() => setEditModes(prev => ({ ...prev, fop: true }))}
          value={limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP]}
          label="Límite de liquidación FOP"
          name={OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP}
          control={control}
          loadOptions={loadLimitesOptions}
          onModeChange={(isFreeSolo) => setFieldModes(prev => ({ ...prev, fop: isFreeSolo }))}
          disabled={isSaving}
        />
      </TableCell>
      <TableCell sx={{ fontSize: '0.875rem' }}>{dateFormatter.toShortDate(new Date())}</TableCell>
    </TableRow>
  );
};
